// ============================================================================
// BESPOKIAN Health - Privacy Policy Page
// Expanded legal page with FAQ-style accordion
// ============================================================================

import { useState } from 'react';
import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { Link } from '@tanstack/react-router';
import { Shield, Eye, FileText, Users, Bell, Trash2, Mail, ChevronDown, Globe, Phone, MessageCircle } from 'lucide-react';

const privacyCategories = [
  {
    title: 'Information Collection',
    icon: FileText,
    items: [
      {
        question: 'What information do you collect?',
        answer: `We collect information you provide directly to us, including:
• Personal identification: Name, email address, phone number, mailing address, date of birth
• Prescription information: Medication names, dosages, quantities, prescribing physician details
• Medical history: Allergies, medical conditions, and other health information relevant to your prescriptions
• Payment information: Credit card details (processed securely via Stripe), billing address
• Insurance information: Insurance card details, policy numbers, group numbers (if applicable)
• Account information: Username, password (encrypted), account preferences
• Communications: Messages, emails, and phone call recordings with our support team
• Device information: IP address, browser type, device identifiers (for security and analytics)`,
      },
      {
        question: 'Do you collect information automatically?',
        answer: `Yes, we automatically collect certain information when you use our website:
• Usage data: Pages visited, time spent, click patterns, search queries
• Technical data: Browser type, operating system, device type, screen resolution
• Location data: General location based on IP address (city/state level, not precise)
• Cookies and tracking: We use cookies to remember your preferences and improve your experience
• Analytics: We use tools like Google Analytics to understand how visitors use our site

You can control cookies through your browser settings, though some features may not work if cookies are disabled.`,
      },
      {
        question: 'Do you collect information from third parties?',
        answer: `We may receive information from:
• Healthcare providers: Prescription information sent electronically
• Insurance companies: Eligibility and coverage information
• Other pharmacies: When transferring prescriptions
• Credit bureaus: For identity verification (with your consent)
• Public databases: For address verification and fraud prevention

We only collect information necessary to provide pharmacy services and comply with legal requirements.`,
      },
    ],
  },
  {
    title: 'How We Use Information',
    icon: Eye,
    items: [
      {
        question: 'How do you use my personal information?',
        answer: `We use your information to:
• Fill and deliver prescriptions: Process orders, verify prescriptions, prepare medications, coordinate shipping
• Provide healthcare services: Consult with pharmacists, manage medication therapy, provide health counseling
• Process payments: Charge your payment method, process insurance claims, handle refunds
• Communicate with you: Send order confirmations, shipping updates, refill reminders, health alerts
• Customer support: Respond to questions, resolve issues, provide pharmacist assistance
• Legal compliance: Meet pharmacy licensing requirements, comply with FDA regulations, respond to legal requests
• Service improvement: Analyze usage patterns, improve website functionality, develop new features
• Security: Detect and prevent fraud, protect against unauthorized access, maintain system security`,
      },
      {
        question: 'Do you use my information for marketing?',
        answer: `We may use your information for marketing purposes with your consent:
• Email newsletters: Health tips, medication information, special offers (you can unsubscribe anytime)
• Promotional communications: Discounts, new service announcements (opt-out available)
• Personalized recommendations: Medication reminders, refill suggestions based on your history

We will NEVER sell your personal information to third parties for marketing purposes. You can opt out of marketing communications at any time through your account settings or by clicking unsubscribe in emails.`,
      },
      {
        question: 'How do you use cookies and tracking?',
        answer: `We use cookies and similar technologies for:
• Essential functions: Remembering login status, shopping cart contents, site preferences
• Analytics: Understanding how visitors use our site to improve user experience
• Security: Detecting fraudulent activity, preventing unauthorized access
• Personalization: Showing relevant content and recommendations

We use both session cookies (deleted when you close your browser) and persistent cookies (remain until deleted). You can manage cookie preferences in your browser settings.`,
      },
    ],
  },
  {
    title: 'Information Protection',
    icon: Shield,
    items: [
      {
        question: 'How do you protect my information?',
        answer: `We implement multiple layers of security:
• Encryption: 256-bit SSL/TLS encryption for all data transmission, AES-256 encryption for data at rest
• HIPAA compliance: Full compliance with Health Insurance Portability and Accountability Act requirements
• Access controls: Role-based access, multi-factor authentication for staff, regular access audits
• Secure infrastructure: Hosted on AWS with SOC 2 Type II certified data centers
• Regular security audits: Third-party penetration testing, vulnerability assessments, security reviews
• Employee training: Comprehensive HIPAA and security training for all staff members
• Incident response: 24/7 security monitoring, automated threat detection, rapid response procedures
• Data backup: Encrypted backups stored in geographically distributed locations`,
      },
      {
        question: 'What happens if there is a data breach?',
        answer: `In the unlikely event of a data breach:
• Immediate response: We will immediately investigate and contain the breach
• Notification: We will notify affected individuals within 72 hours as required by HIPAA
• Regulatory reporting: We will report to HHS and state authorities as required by law
• Credit monitoring: We may offer free credit monitoring services if financial information is compromised
• Remediation: We will take steps to prevent future breaches and address any vulnerabilities

We maintain cyber liability insurance and have incident response procedures in place.`,
      },
      {
        question: 'How do you protect payment information?',
        answer: `Payment security measures:
• PCI DSS compliance: We comply with Payment Card Industry Data Security Standards
• Tokenization: Credit card numbers are tokenized and never stored in our systems
• Stripe integration: All payment processing handled by Stripe, a PCI Level 1 certified processor
• No storage: We do not store full credit card numbers on our servers
• Secure transmission: All payment data encrypted in transit using TLS 1.3
• Fraud detection: Real-time fraud monitoring and suspicious activity alerts

Your payment information is processed securely and we never have access to your full card details.`,
      },
    ],
  },
  {
    title: 'Information Sharing',
    icon: Users,
    items: [
      {
        question: 'Who do you share my information with?',
        answer: `We may share your information with:
• Healthcare providers: Your prescribing physicians, specialists, and other healthcare professionals involved in your care
• Insurance companies: For claims processing, eligibility verification, and prior authorization requests
• Pharmacy benefit managers (PBMs): When processing insurance claims
• Shipping carriers: UPS, FedEx, USPS for package delivery (name, address, tracking)
• Service providers: IT vendors, cloud hosting providers, payment processors (under strict confidentiality agreements)
• Business partners: Telemedicine providers, lab services (with your explicit consent)
• Legal authorities: When required by law, court order, or regulatory investigation
• Emergency situations: In medical emergencies to protect your health and safety

We only share the minimum information necessary and require all third parties to maintain confidentiality.`,
      },
      {
        question: 'Do you sell my information?',
        answer: `NO. We do NOT sell your personal information, including:
• We do not sell your name, email, phone number, or address
• We do not sell your prescription or medical information
• We do not sell your payment information
• We do not sell your browsing or usage data

This is a core commitment of our privacy policy. Your information is only shared as described above for legitimate business and legal purposes.`,
      },
      {
        question: 'What about business transfers?',
        answer: `In the event of a merger, acquisition, or sale of assets:
• Your information may be transferred as part of the business transaction
• The new owner will be bound by this privacy policy
• You will be notified of any material changes to how your information is used
• You will have the right to opt out or request deletion if you disagree with new practices

We will provide at least 30 days notice before any such transfer takes effect.`,
      },
    ],
  },
  {
    title: 'Your Rights & Choices',
    icon: Bell,
    items: [
      {
        question: 'What are my privacy rights?',
        answer: `You have the right to:
• Access: Request a copy of all personal information we hold about you
• Correction: Request correction of inaccurate or incomplete information
• Deletion: Request deletion of your information (subject to legal retention requirements)
• Portability: Request your data in a machine-readable format
• Opt-out: Opt out of marketing communications and certain data uses
• Restriction: Request restriction of how we process your information
• Objection: Object to certain types of processing

To exercise these rights, contact us at privacy@bespokianhealth.com or through your account settings.`,
      },
      {
        question: 'How do I update my information?',
        answer: `You can update your information:
• Online: Log into your account and update your profile, address, phone number, and preferences
• By phone: Call our customer service team at 1-877-BES-PORX
• By email: Send updates to support@bespokianhealth.com
• By mail: Send written requests to our Privacy Officer (address below)

For prescription information, contact your prescribing physician. For insurance information, contact your insurance company.`,
      },
      {
        question: 'Can I delete my account?',
        answer: `Yes, you can request account deletion:
• Through your account: Go to Settings > Account > Delete Account
• By email: Send a request to privacy@bespokianhealth.com
• By phone: Call customer service

Important notes:
• Prescription records must be retained per state pharmacy laws (typically 2-10 years)
• We will delete all other personal information within 30 days
• Some information may be retained in anonymized form for legal compliance
• Deletion is permanent and cannot be undone

We will confirm deletion in writing within 30 days.`,
      },
      {
        question: 'How do I opt out of marketing?',
        answer: `You can opt out of marketing communications:
• Email: Click "unsubscribe" in any marketing email
• Account settings: Disable marketing communications in your account preferences
• Phone: Tell our customer service representative when they call
• Email request: Send to unsubscribe@bespokianhealth.com

Note: You cannot opt out of transactional communications (order confirmations, shipping updates, health alerts, prescription reminders) as these are essential for your care.`,
      },
    ],
  },
  {
    title: 'Data Retention & Deletion',
    icon: Trash2,
    items: [
      {
        question: 'How long do you keep my information?',
        answer: `We retain information as required by law and business needs:
• Prescription records: 2-10 years (varies by state pharmacy law requirements)
• Account information: Until account deletion is requested
• Financial records: 7 years (IRS and accounting requirements)
• Legal records: As required by ongoing legal proceedings or investigations
• Analytics data: 2 years in identifiable form, then anonymized
• Security logs: 1 year for fraud prevention and security monitoring

After retention periods expire, we securely delete or anonymize your information.`,
      },
      {
        question: 'What happens to my data when I delete my account?',
        answer: `When you delete your account:
• Immediate deletion: Account credentials, profile information, preferences
• Legal retention: Prescription records retained per state law (2-10 years)
• Anonymization: Usage data may be anonymized and retained for analytics
• Backup retention: Encrypted backups may retain data for up to 90 days

We will provide a written confirmation of what was deleted and what was retained.`,
      },
    ],
  },
  {
    title: 'Children\'s Privacy',
    icon: Shield,
    items: [
      {
        question: 'Do you collect information from children?',
        answer: `Our services are not intended for children under 18:
• We do not knowingly collect personal information from children under 18
• If we discover we have collected information from a child, we will delete it immediately
• Parents or guardians must create accounts and manage prescriptions for minors
• We comply with COPPA (Children's Online Privacy Protection Act) requirements

If you believe we have collected information from a child, contact us immediately at privacy@bespokianhealth.com.`,
      },
    ],
  },
  {
    title: 'International Users',
    icon: Globe,
    items: [
      {
        question: 'Where is my data stored?',
        answer: `Data storage and processing:
• Primary location: United States (AWS data centers)
• Backup locations: Geographically distributed within the United States
• International transfers: We do not transfer data outside the United States
• GDPR: While we primarily serve U.S. customers, we comply with applicable international privacy laws

By using our services, you consent to the processing of your information in the United States.`,
      },
    ],
  },
];

// Helper function to calculate word count and reading time
function getReadingStats(text: string) {
  const words = text.trim().split(/\s+/).filter(word => word.length > 0).length;
  const readingTime = Math.max(1, Math.ceil(words / 200)); // 200 words per minute
  return { words, readingTime };
}

export function PrivacyPage() {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  const toggleItem = (key: string) => {
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />

      <main className="flex-1 pt-24">
        {/* Hero */}
        <section className="bg-gradient-to-br from-navy-50 via-white to-sage-50/30 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />

            <p className="text-gold-600 font-medium tracking-wide uppercase text-sm mb-4">
              Legal
            </p>

            <h1 className="font-display text-4xl md:text-5xl text-navy-800 tracking-tight leading-tight mb-6 italic">
              Privacy Policy
            </h1>

            <p className="text-xl text-navy-600 max-w-2xl mx-auto">
              Your privacy is important to us. Learn how we collect, use, and protect your information.
            </p>

            <p className="text-sm text-navy-400 mt-4">Last updated: January 2026</p>
          </div>
        </section>

        {/* Content - FAQ Style Accordion */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {privacyCategories.map((category, categoryIndex) => (
              <div key={category.title} className={categoryIndex > 0 ? 'mt-12' : ''}>
                {/* Category Header */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
                    <category.icon className="h-5 w-5 text-sage-600" />
                  </div>
                  <div className="w-8 h-0.5 bg-gold-500" />
                  <h2 className="font-display text-xl text-navy-800 tracking-tight">{category.title}</h2>
                </div>

                {/* Accordion Items */}
                <div className="space-y-3">
                  {category.items.map((item, itemIndex) => {
                    const key = `${categoryIndex}-${itemIndex}`;
                    const isOpen = openItems[key];
                    const { words, readingTime } = getReadingStats(item.answer);

                    return (
                      <div
                        key={key}
                        className={`border rounded-xl overflow-hidden transition-colors ${
                          isOpen ? 'border-gold-300 bg-gold-50/30' : 'border-navy-100 hover:border-navy-200'
                        }`}
                      >
                        <button
                          className="w-full px-6 py-4 text-left"
                          onClick={() => toggleItem(key)}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-navy-800 block">{item.question}</span>
                              {/* Mobile: Show stats under question */}
                              <div className="lg:hidden mt-2">
                                <span className="text-xs text-navy-400">
                                  {words} words • {readingTime} min read
                                </span>
                              </div>
                            </div>
                            {/* Desktop: Show stats next to question */}
                            <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
                              <span className="text-xs text-navy-400 whitespace-nowrap">
                                {words} words • {readingTime} min read
                              </span>
                              <ChevronDown
                                className={`h-5 w-5 text-navy-400 transition-transform flex-shrink-0 ${
                                  isOpen ? 'transform rotate-180 text-gold-600' : ''
                                }`}
                              />
                            </div>
                            {/* Mobile: Show chevron */}
                            <ChevronDown
                              className={`lg:hidden h-5 w-5 text-navy-400 transition-transform flex-shrink-0 mt-0.5 ${
                                isOpen ? 'transform rotate-180 text-gold-600' : ''
                              }`}
                            />
                          </div>
                        </button>
                        {isOpen && (
                          <div className="px-6 pb-4">
                            <p className="text-navy-600 leading-relaxed whitespace-pre-line">{item.answer}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Contact Section */}
            <div className="mt-12 p-6 bg-gold-50 rounded-xl border border-gold-200">
              <div className="flex items-start gap-4">
                <Mail className="h-6 w-6 text-gold-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-navy-800 mb-2">Questions About Privacy?</h3>
                  <p className="text-navy-600 mb-4">
                    Contact our Privacy Officer at{' '}
                    <a href="mailto:privacy@bespokianhealth.com" className="text-gold-600 hover:text-gold-700 font-medium">
                      privacy@bespokianhealth.com
                    </a>
                  </p>
                  <p className="text-sm text-navy-500">
                    BESPOKIAN Health, Attn: Privacy Officer<br />
                    123 Pharmacy Way, Altamonte Springs, FL
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Options */}
        <section className="py-16 bg-navy-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />
              <h2 className="font-display text-3xl text-navy-800 tracking-tight italic mb-4">Need Help?</h2>
              <p className="text-navy-600">Our privacy team is here to assist you.</p>
            </div>

            <div className="grid sm:grid-cols-3 gap-6">
              {/* Phone */}
              <a
                href="tel:1-877-237-7679"
                className="bg-white rounded-xl p-6 text-center shadow-card hover:shadow-card-hover transition-shadow"
              >
                <div className="w-12 h-12 bg-sage-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-6 w-6 text-sage-600" />
                </div>
                <h3 className="font-semibold text-navy-800 mb-1">Call Us</h3>
                <p className="text-navy-600 text-sm mb-2">Privacy Questions</p>
                <p className="text-gold-600 font-medium">1-877-BES-PORX</p>
              </a>

              {/* Email */}
              <a
                href="mailto:privacy@bespokianhealth.com"
                className="bg-white rounded-xl p-6 text-center shadow-card hover:shadow-card-hover transition-shadow"
              >
                <div className="w-12 h-12 bg-gold-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-6 w-6 text-gold-600" />
                </div>
                <h3 className="font-semibold text-navy-800 mb-1">Email Us</h3>
                <p className="text-navy-600 text-sm mb-2">Response within 24 hours</p>
                <p className="text-gold-600 font-medium">privacy@bespokianhealth.com</p>
              </a>

              {/* Chat */}
              <Link
                to="/contact"
                className="bg-white rounded-xl p-6 text-center shadow-card hover:shadow-card-hover transition-shadow"
              >
                <div className="w-12 h-12 bg-navy-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="h-6 w-6 text-navy-600" />
                </div>
                <h3 className="font-semibold text-navy-800 mb-1">Contact Form</h3>
                <p className="text-navy-600 text-sm mb-2">Submit a privacy request</p>
                <p className="text-gold-600 font-medium">Contact Us</p>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

